import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import './index.css';
import { AuthProvider } from '@/context/AuthContext';
import { VaultSessionProvider } from '@/context/VaultSessionContext';
import { SocketProvider } from '@/context/SocketContext';
import ErrorBoundary from './components/ErrorBoundary';
import { initErrorHandling } from './utils/errorHandler';
import { initErrorTracker } from './utils/errorTracker';
import { UIProvider } from '@gff/ui';

// Initialize error tracking to identify the source of the [ERROR] {} pattern
initErrorTracker();

// Directly intercept the specific [ERROR] {} pattern before anything else runs
const originalConsoleError = console.error;
console.error = function (...args) {
  // Check if this matches the exact [ERROR] {} pattern
  if (args.length === 2 && args[0] === '[ERROR]' &&
    (args[1] === '{}' ||
      (typeof args[1] === 'object' && Object.keys(args[1]).length === 0))) {
    console.debug('[INTERCEPTED] Empty error object suppressed');
    // Log the call stack to help identify where this is coming from
    console.debug('Call stack:', new Error().stack);
    return;
  }
  // Otherwise, pass through to the original console.error
  originalConsoleError.apply(console, args);
};

// Initialize the rest of the global error handling
initErrorHandling();

// Configure React Query client with robust error handling
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      // Prevent empty error objects from being logged
      onError: (error) => {
        // Skip logging empty error objects completely
        if (!error ||
          (typeof error === 'object' && Object.keys(error).length === 0) ||
          error === '{}') {
          console.debug('Empty error object intercepted in React Query');
          return;
        }
        console.error('React Query error:', error);
      },
      // Add stale time to prevent unnecessary refetches during navigation
      staleTime: 30000, // 30 seconds
      // Add cache time to keep data in cache longer
      cacheTime: 5 * 60 * 1000, // 5 minutes
    },
    mutations: {
      retry: 0,
      onError: (error) => {
        // Skip logging empty error objects completely
        if (!error ||
          (typeof error === 'object' && Object.keys(error).length === 0) ||
          error === '{}') {
          console.debug('Empty error object intercepted in React Query mutation');
          return;
        }
        console.error('React Query mutation error:', error);
      }
    }
  }
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <VaultSessionProvider>
              <SocketProvider>
                <UIProvider>
                  <App />
                </UIProvider>
              </SocketProvider>
            </VaultSessionProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
